import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

export const InterProceso = () => {
  const toast = useToast();
  const { auth } = useAuth();

  const [ticajes, setTicajes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  const esAdmin = auth?.rol === "admin";
  const esGerente = auth?.rol === "gerente";

  useEffect(() => {
    if (!auth?.id) return;
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.id]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

      const needsUsuarios = esGerente;
      const requests = [
        fetch(`${Global.url}ticajes/proceso`, { headers }),
        needsUsuarios ? fetch(`${Global.url}usuarios`, { headers }) : Promise.resolve(null),
      ];

      const [resTicajes, resUsuarios] = await Promise.all(requests);

      let dataUsuarios = [];
      if (resUsuarios) {
        dataUsuarios = resUsuarios.ok ? await resUsuarios.json() : [];
        setUsuarios(Array.isArray(dataUsuarios) ? dataUsuarios : []);

        if (!resUsuarios.ok) {
          toast.warning("No se pudieron cargar los usuarios (se mostrarán IDs).");
        }
      } else {
        setUsuarios([]);
      }

      if (!resTicajes.ok) {
        toast.error("No se pudieron obtener las intervenciones en proceso.");
        setTicajes([]);
        return;
      }

      const dataTicajes = await resTicajes.json();
      const ticajesAll = Array.isArray(dataTicajes) ? dataTicajes : [];

      if (esAdmin) {
        setTicajes(ticajesAll);
        return;
      }

      if (esGerente) {
        const idsEquipo = new Set(
          (Array.isArray(dataUsuarios) ? dataUsuarios : [])
            .filter((u) => String(u.gerente_id) === String(auth?.id))
            .map((u) => String(u.id))
        );

        const ticajesEquipo = ticajesAll.filter((t) => idsEquipo.has(String(t.usuario_id)));
        setTicajes(ticajesEquipo);
        return;
      }

      setTicajes([]);
    } catch (error) {
      console.error("Error al cargar los ticajes:", error);
      toast.error("Error de conexión con el servidor.");
      setTicajes([]);
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const getNombreCompleto = (usuario_id) => {
    const u = usuarios.find((x) => String(x.id) === String(usuario_id));
    return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : `ID ${usuario_id ?? "-"}`;
  };

  const parseDateSafe = (value) => {
    if (!value) return null;

    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";

    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const rows = useMemo(
    () =>
      (ticajes || []).map((t) => ({
        ...t,
        usuario_nombre: t.usuario || (esGerente ? getNombreCompleto(t.usuario_id) : t.usuario) || `ID ${t.usuario_id ?? "-"}`,
        inicio_txt: fmtDate(t.inicio),
        comentarios_txt: t.comentarios?.trim() ? t.comentarios : "Sin comentarios",
      })),
    [ticajes, usuarios, esGerente]
  );

  const columns = useMemo(
    () => [
      { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 220 },
      { field: "centro", headerName: "Centro", flex: 1, minWidth: 220 },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 180 },
      { field: "comentarios_txt", headerName: "Comentarios", flex: 1, minWidth: 260 },
    ],
    []
  );

  const titulo = esAdmin ? "Intervenciones en Proceso" : "Intervenciones en Proceso de mi equipo";

  const textoSinFilas = esAdmin
    ? "No hay intervenciones activas actualmente."
    : "No hay intervenciones activas de tu equipo.";

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>

        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : textoSinFilas}
                </Box>
              ),
            }}
          />
        </Paper>
      </Stack>
    </Box>
  );
};